import React, { useRef } from 'react';

import UserChatItem from './atoms/UserChatItem';
import { useEffect, useState } from 'react';
import { SearchNormal, ArrowDown } from 'iconsax-react';
import ChatFilterItem from './atoms/ChatFilterItem';
import { UserInChat } from '../../../types/chat';
import {
  setCanCancelFriendlyStage,
  setDisputeId,
  setIsClosed,
  setIsGrouping,
  setSelectedAllUsers,
  setSelectedUser,
  useChat,
} from '../../../store/slices/chat';
import { navbarSocket } from '../../../lib/socket';

import TitleText from '../../../components/atoms/TitleText';
import Input from '../../../components/atoms/Input';
import LoaderSpin from '../../../components/atoms/LoaderSpin';
import { useTranslation } from 'react-i18next';
import { useGetAllUserInChatQuery } from '../../../store/services/chatService';
import { Empty } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../../store/store';

const Sidebar = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const chatRoomId = searchParams.get('chatRoomId');

  const filterItems = [
    {
      text: t('all'),
      value: 'ALL',
    },
    {
      text: t('read'),
      value: 'READ',
    },
    {
      text: t('unread'),
      value: 'UNREAD',
    },
  ];
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();

  const { messageStatusFilter, selectedRoomId, selectedUser } = useChat();
  const effectiveRoomId = chatRoomId || selectedRoomId;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allUsers, setAllUsers] = useState<UserInChat[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.adminData?.id;
  const token = useSelector((state: RootState) => state.auth.token);

  const {
    data: allUsersInChat,
    isFetching,
    isLoading,
  } = useGetAllUserInChatQuery(
    {
      search: search?.trim() ? search.trim() : undefined,
      status: messageStatusFilter?.toUpperCase() === 'ALL' ? undefined : messageStatusFilter,
      page: page.toString(),
    },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    const chatData = allUsersInChat?.payload ?? allUsersInChat;
    const items = chatData?.content ?? (Array.isArray(chatData) ? chatData : null);
    if (items) {
      if (page === 0) {
        setAllUsers(items);
      } else {
        setAllUsers((prev) => [...prev, ...items]);
      }
      const totalPages = chatData?.totalPages ?? 1;
      setHasMore(totalPages > page + 1);
      setIsLoadingMore(false);
    }
  }, [allUsersInChat]);

  useEffect(() => {
    if (allUsers && effectiveRoomId) {
      const selectedChat = allUsers.find(
        (users) => users?.roomId === effectiveRoomId
      );
      if (selectedChat) {
        if (!selectedUser || selectedUser?.roomId !== effectiveRoomId) {
          dispatch(setSelectedUser(selectedChat));
          if (selectedChat.users) {
            dispatch(setSelectedAllUsers(selectedChat.users));
          }
        }
        dispatch(setIsGrouping(Boolean(selectedChat?.isGrouping)));
        dispatch(setIsClosed(selectedChat?.isClosed));
        dispatch(
          setCanCancelFriendlyStage(
            selectedChat?.actionsDto?.canCancelFriendlyStage as boolean
          )
        );
      }
    }
  }, [allUsers, effectiveRoomId]);

  // Reset pagination when search or filter changes
  useEffect(() => {
    setPage(0);
    setHasMore(false);
  }, [search, messageStatusFilter]);

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  const handleSeenOnClick = (roomId: string | number) => {
    const newAllUsers = allUsers?.map((item) => {
      if (item?.roomId === roomId)
        return {
          ...item,
          unRead: false,
        };
      return item;
    });
    setAllUsers(newAllUsers);
  };

  useEffect(() => {
    if (token && userId) {
      navbarSocket.io.opts.query = {
        profileId: userId,
        token: token,
      };

      navbarSocket.connect();
    }

    navbarSocket.on('user-chats', (userSent) => {
      const parsedUser: UserInChat =
        typeof userSent === 'string' ? JSON.parse(userSent) : userSent;
      console.log('parsedUser', parsedUser);
      // Move the incoming user to the first position
      setAllUsers((prevUsers) => {
        // Remove the user if they already exist in the array
        let filteredUsers = [];
        if (parsedUser?.isGrouping) {
          filteredUsers = prevUsers.filter((user) => {
            return user?.roomId !== parsedUser?.roomId;
          });
        } else {
          filteredUsers = prevUsers.filter((user) => {
            return user?.users?.[0]?.id !== parsedUser?.users?.[0]?.id;
          });
        }

        // Add the user to the beginning of the array
        return [parsedUser, ...filteredUsers];
      });
    });

    return () => {
      navbarSocket.off('user-chats');
    };
  }, [token]);

  // useEffect(() => {
  //   if (selectedRoomId && allUsers?.length) {
  //     const roomData = allUsers?.find(
  //       (item) => item?.roomId === selectedRoomId
  //     );
  //     console.log('roomData', roomData);
  //     if (roomData) {
  //       dispatch(setSelectedUser(userData));
  //       dispatch(setSelectedAllUsers(roomData?.users));
  //       dispatch(setIsGrouping(roomData?.isGrouping as boolean));
  //       dispatch(setDisputeId(roomData?.disputeId as string));
  //       dispatch(
  //         setCanCancelFriendlyStage(
  //           //@ts-expect-error
  //           roomData?.actionsDto?.canCancelFriendlyStageroomData || undefined
  //         )
  //       );
  //       dispatch(setIsClosed(roomData?.isClosed as boolean));
  //     }
  //   }
  // }, [selectedRoomId, allUsers]);

  return (
    <div className="h-full flex flex-col ">
      <div className="flex flex-col gap-4">
        <TitleText text={t('messages')} className="text-left" />
        <Input
          classNameContainer="max-w-full"
          id="search"
          placeholder={t('searchForYourNeeds')}
          value={search || ''}
          onChange={(e) => {
            const eValue = e.target.value;
            if (eValue && eValue !== '') {
              setSearch(e.target.value);
            } else {
              setSearch('');
            }
          }}
          suffix={
            <div className="p-2 border-l ">
              {' '}
              <SearchNormal color="#090909" size={15} />{' '}
            </div>
          }
          size="large"
          className="max-w-full"
        />

        <div className="flex flex-wrap gap-4">
          {filterItems?.map((item, index) => (
            <ChatFilterItem {...item} key={index} />
          ))}
        </div>
      </div>

      <div ref={contentRef} className="overflow-y-auto flex-1 min-h-0 py-2">
        {(isFetching || isLoading) && page === 0 ? (
          <LoaderSpin />
        ) : (
          <>
            {allUsers?.length ? (
              allUsers?.map((oneUser) => {
                return (
                  <UserChatItem
                    handleSeenOnClick={handleSeenOnClick}
                    key={oneUser?.id}
                    {...oneUser}
                  />
                );
              })
            ) : (
              <Empty description="No Messages here.." />
            )}
            {hasMore && !isFetching && !isLoading && (
              <div className="flex justify-center py-2">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="flex items-center gap-2 p-4 rounded-full bg-soft-background dark:bg-soft-dark-background text-light-text-primary dark:text-dark-text-primary hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDown size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
